// MobileSignup — tela de CRIAR CONTA mobile (parede A1). Email + senha +
// confirmar senha (+ Google). Reusa POST /register; ao criar, o backend já
// devolve tokens → o motor persiste a sessão e entra direto.
//
// Touch-first, sem foco de controle remoto. Validação local: email válido, senha
// >= 8 (mesmo mínimo do backend) e confirmação batendo — feedback imediato sem
// ida desnecessária ao servidor.

import { useTranslation } from '@app-iptv/i18n'
import { useState } from 'react'

import { registerWithEmail, type AuthErrorCode } from '../auth/account-auth'

import { authErrorKey, googleErrorKey } from './auth-messages'
import { GoogleButton } from './GoogleButton'
import styles from './MobileAuth.module.css'
import { MobileBrandLockup } from './MobileBrandLockup'

interface MobileSignupProps {
  /** Cadastro (email ou Google) concluído — sessão Swipe já persistida. */
  onAuthenticated: () => void
  /** Voltar pra tela de login. */
  onGoToLogin: () => void
}

const MIN_PASSWORD = 8 // bate com SwipeRegisterBody (z.string().min(8))

function looksLikeEmail(value: string): boolean {
  return /.+@.+\..+/.test(value.trim())
}

export function MobileSignup({ onAuthenticated, onGoToLogin }: MobileSignupProps): JSX.Element {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [errorKey, setErrorKey] = useState<string | null>(null)
  const [fieldError, setFieldError] = useState<'email' | 'password' | 'confirm' | null>(null)

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    if (busy) return
    setErrorKey(null)
    setFieldError(null)

    if (!looksLikeEmail(email)) {
      setFieldError('email')
      setErrorKey('mobileAuth.error.invalidEmail')
      return
    }
    if (password.length < MIN_PASSWORD) {
      setFieldError('password')
      setErrorKey('mobileAuth.error.passwordTooShort')
      return
    }
    if (password !== confirm) {
      setFieldError('confirm')
      setErrorKey('mobileAuth.error.passwordMismatch')
      return
    }

    setBusy(true)
    try {
      const result = await registerWithEmail(email, password)
      if (result.ok) onAuthenticated()
      else setErrorKey(authErrorKey(result.error))
    } finally {
      setBusy(false)
    }
  }

  const onAuthError = (code: AuthErrorCode): void => setErrorKey(authErrorKey(code))
  const onGoogleError = (code: 'load_failed' | 'unavailable'): void =>
    setErrorKey(googleErrorKey(code))

  return (
    <div className={styles.screen}>
      <div className={styles.content}>
        <header className={styles.header}>
          <span className={styles.brandLogo}>
            <MobileBrandLockup height={48} />
          </span>
          <h1 className={styles.title}>{t('mobileAuth.signup.title')}</h1>
          <p className={styles.subtitle}>{t('mobileAuth.signup.subtitle')}</p>
        </header>

        <form className={styles.form} onSubmit={(e) => void handleSubmit(e)} noValidate>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="mobile-signup-email">
              {t('mobileAuth.emailLabel')}
            </label>
            <input
              id="mobile-signup-email"
              className={`${styles.input} ${fieldError === 'email' ? styles.inputError : ''}`}
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              placeholder={t('mobileAuth.emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={busy}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="mobile-signup-password">
              {t('mobileAuth.passwordLabel')}
            </label>
            <input
              id="mobile-signup-password"
              className={`${styles.input} ${fieldError === 'password' ? styles.inputError : ''}`}
              type="password"
              autoComplete="new-password"
              placeholder={t('mobileAuth.signup.passwordPlaceholder')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={busy}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="mobile-signup-confirm">
              {t('mobileAuth.signup.confirmLabel')}
            </label>
            <input
              id="mobile-signup-confirm"
              className={`${styles.input} ${fieldError === 'confirm' ? styles.inputError : ''}`}
              type="password"
              autoComplete="new-password"
              placeholder={t('mobileAuth.signup.confirmPlaceholder')}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              disabled={busy}
            />
          </div>

          {errorKey && (
            <p className={styles.error} role="alert">
              {t(errorKey)}
            </p>
          )}

          <button type="submit" className={`${styles.button} ${styles.primary}`} disabled={busy}>
            {busy ? (
              <span className={styles.spinner} aria-hidden="true" />
            ) : (
              t('mobileAuth.signup.submit')
            )}
          </button>
        </form>

        <div className={styles.divider}>{t('mobileAuth.or')}</div>

        <GoogleButton
          busy={busy}
          onBusyChange={setBusy}
          onSuccess={onAuthenticated}
          onAuthError={onAuthError}
          onGoogleError={onGoogleError}
        />

        <div className={styles.links}>
          <div className={styles.linkRow}>
            {t('mobileAuth.signup.haveAccount')}{' '}
            <button type="button" className={styles.linkButton} onClick={onGoToLogin} disabled={busy}>
              {t('mobileAuth.signup.backToLogin')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
